export type DimensionAxis = 'width' | 'height' | 'depth';
export type DimensionUnit = 'mm' | 'm';
export type WeightUnit = 'kg' | 'g';

export type DataConventionChangeReason =
  | 'InitialSetup'
  | 'PolicyChange'
  | 'ClientRequest'
  | 'Correction';

export const ALL_DATA_CONVENTION_CHANGE_REASONS: readonly DataConventionChangeReason[] = [
  'InitialSetup',
  'PolicyChange',
  'ClientRequest',
  'Correction',
] as const;

export const USER_SELECTABLE_DATA_CONVENTION_CHANGE_REASONS: readonly DataConventionChangeReason[] = [
  'PolicyChange',
  'ClientRequest',
  'Correction',
] as const;

export const DEFAULT_DATA_CONVENTION_CHANGE_REASON: DataConventionChangeReason = 'PolicyChange';

const REASON_LABEL: Record<DataConventionChangeReason, string> = {
  InitialSetup: 'Initial setup',
  PolicyChange: 'Policy change',
  ClientRequest: 'Client request',
  Correction: 'Correction',
};

export const dataConventionChangeReasonLabel = (reason: DataConventionChangeReason): string =>
  REASON_LABEL[reason];

export type DimensionOrder =
  | 'WidthHeightDepth'
  | 'WidthDepthHeight'
  | 'HeightWidthDepth'
  | 'HeightDepthWidth'
  | 'DepthWidthHeight'
  | 'DepthHeightWidth';

export interface DataConvention {
  dimensionOrder: DimensionOrder;
  dimensionUnit: DimensionUnit;
  weightUnit: WeightUnit;
}

export const DEFAULT_DATA_CONVENTION: DataConvention = {
  dimensionOrder: 'WidthDepthHeight',
  dimensionUnit: 'mm',
  weightUnit: 'kg',
};

export const ALL_DIMENSION_ORDERS: readonly DimensionOrder[] = [
  'WidthHeightDepth',
  'WidthDepthHeight',
  'HeightWidthDepth',
  'HeightDepthWidth',
  'DepthWidthHeight',
  'DepthHeightWidth',
] as const;

export const ALL_DIMENSION_UNITS: readonly DimensionUnit[] = ['mm', 'm'] as const;

export const ALL_WEIGHT_UNITS: readonly WeightUnit[] = ['kg', 'g'] as const;

const AXIS_LIST_BY_ORDER: Record<DimensionOrder, readonly DimensionAxis[]> = {
  WidthHeightDepth: ['width', 'height', 'depth'],
  WidthDepthHeight: ['width', 'depth', 'height'],
  HeightWidthDepth: ['height', 'width', 'depth'],
  HeightDepthWidth: ['height', 'depth', 'width'],
  DepthWidthHeight: ['depth', 'width', 'height'],
  DepthHeightWidth: ['depth', 'height', 'width'],
};

export const axesFor = (order: DimensionOrder): readonly DimensionAxis[] => AXIS_LIST_BY_ORDER[order];

const AXIS_LABEL: Record<DimensionAxis, string> = {
  width: 'Width',
  height: 'Height',
  depth: 'Depth',
};

export const dimensionOrderLabel = (order: DimensionOrder): string =>
  axesFor(order).map((axis: DimensionAxis) => AXIS_LABEL[axis]).join(' × ');
